import { Fragment, useMemo, useState, type JSX } from "react";
import { Link, useLocation } from "react-router";

import { useMeQuery } from "../../lib/api/auth.js";
import { cn } from "../../lib/cn.js";

import { activeNavTo, visibleNavSections } from "./nav-config.js";

const RAIL_WIDTH_CLASS = "w-20"; // 5rem = 80px to match screenshot
const PANEL_WIDTH_CLASS = "w-24"; // narrow second-level flyout (icon + label)

export function AppSidebar(): JSX.Element {
  const location = useLocation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const isAdmin = useMeQuery().data?.role === "tenant_admin";
  const navSections = useMemo(() => visibleNavSections(isAdmin), [isAdmin]);
  const allNavItems = useMemo(() => navSections.flatMap((s) => s.items), [navSections]);

  // The single most-specific active route, and the section that owns it.
  const activeTo = useMemo(
    () => activeNavTo(location.pathname, allNavItems),
    [location.pathname, allNavItems],
  );
  const activeIndex = useMemo(() => {
    if (activeTo === null) return 0;
    const idx = navSections.findIndex((section) =>
      section.items.some((item) => item.to === activeTo),
    );
    return idx === -1 ? 0 : idx;
  }, [activeTo, navSections]);

  const hoveredCandidate = hoveredIndex === null ? undefined : navSections[hoveredIndex];
  const hoveredSection =
    hoveredCandidate && hoveredCandidate.items.length > 1 ? hoveredCandidate : null;

  return (
    <div
      className="relative hidden md:block"
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {/* ── Level 1: group rail (logo now lives in the header) ──────────── */}
      <aside
        className={cn(
          "flex h-full flex-col bg-sidebar text-sidebar-foreground",
          RAIL_WIDTH_CLASS,
        )}
        aria-label="Primary navigation"
      >
        <nav className="flex-1 overflow-y-auto py-3">
          <ul className="flex flex-col items-center gap-1 px-1">
            {navSections.map((section, idx) => {
              const Icon = section.icon;
              const active = idx === activeIndex;
              return (
                <Fragment key={section.group}>
                  <li className="w-full">
                    <Link
                      to={section.items[0]?.to ?? "/dashboard"}
                      title={section.group}
                      aria-current={active ? "page" : undefined}
                      onMouseEnter={() =>
                        setHoveredIndex(section.items.length > 1 ? idx : null)
                      }
                      onFocus={() => setHoveredIndex(section.items.length > 1 ? idx : null)}
                      className={cn(
                        "group flex flex-col items-center gap-1 rounded-md px-1 py-2 text-[10px] font-medium leading-none transition-colors",
                        "hover:bg-sidebar-accent/60",
                        active
                          ? "bg-sidebar-accent text-sidebar-foreground"
                          : "text-sidebar-foreground/70",
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                      <span className="text-center">{section.group}</span>
                    </Link>
                  </li>
                  {/* Separator after the top-level "home" group. */}
                  {idx === 0 ? (
                    <li aria-hidden="true" className="w-full py-1">
                      <div className="mx-3 border-t border-sidebar-border" />
                    </li>
                  ) : null}
                </Fragment>
              );
            })}
          </ul>
        </nav>

        <div className="px-2 py-3 text-center text-[10px] text-sidebar-foreground/40">
          v0.0.0
        </div>
      </aside>

      {/* ── Level 2: hover flyout (overlay) ─────────────────────────────── */}
      {hoveredSection ? (
        <aside
          // The rail already sits below the header, so the flyout fills the
          // rail's height and overlays the page content beside it.
          className={cn(
            "absolute left-full top-0 z-40 flex h-full flex-col border-l border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl",
            PANEL_WIDTH_CLASS,
          )}
          aria-label={`${hoveredSection.group} navigation`}
        >
          <nav className="flex-1 overflow-y-auto py-3">
            <ul className="flex flex-col items-center gap-1 px-1">
              {hoveredSection.items.map((item) => {
                const Icon = item.icon;
                const active = item.to === activeTo;
                return (
                  <li key={item.to} className="w-full">
                    <Link
                      to={item.to}
                      title={item.label}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-md px-1 py-2.5 text-center text-[10px] font-medium leading-tight transition-colors",
                        active
                          ? "bg-sidebar-accent text-sidebar-foreground"
                          : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>
      ) : null}
    </div>
  );
}
